import React from 'react'
import { Typography, Box } from '@mui/material'
import { withStyles } from '@mui/styles'
import { actionButtonStyles } from './action-button.styles'

/**
 * @typedef {object} ActionButtonProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles` para la estilización.
 * @property {string} label - El texto que se mostrará junto al icono.
 * @property {React.ReactNode | function(): React.ReactNode} icon - El icono a mostrar. Puede ser un elemento de React instanciado (ej. `<AddIcon />`) o una función que devuelve dicho elemento (ej. `() => <AddIcon />`).
 * @property {function(React.MouseEvent<HTMLElement>): void} onClick - La función que se ejecutará cuando el usuario haga clic en el componente.
 */

/**
 * Un componente de UI reutilizable que presenta un icono junto a una etiqueta de texto.
 * Está diseñado para funcionar como un botón de acción, ejecutando una función `onClick`
 * cuando el usuario interactúa con él. La estilización se gestiona a través de JSS y `withStyles`.
 *
 * @component
 * @example
 * import { AddCircleOutline as AddIcon } from '@mui/icons-material';
 *
 * const handleAddNew = () => {
 * console.log('Nuevo elemento añadido');
 * };
 *
 * // Uso con un elemento de React como icono
 * <ActionButton
 * label="Añadir Elemento"
 * icon={<AddIcon />}
 * onClick={handleAddNew}
 * />
 *
 * // Uso con una función que devuelve un icono
 * <ActionButton
 * label="Añadir Elemento"
 * icon={() => <AddIcon color="primary" />}
 * onClick={handleAddNew}
 * />
 *
 * @param {ActionButtonProps} props - Las props del componente ActionButton.
 */
export const ActionButton = withStyles(actionButtonStyles)(({ classes, label, icon, onClick }) => {
  const IconComponent = typeof icon === 'function' ? icon() : icon

  return (
    <Box onClick={onClick} className={classes.root}>
      <Box>{IconComponent}</Box>
      <Box ml={1}>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </Box>
    </Box>
  )
})
